/**
 * The sidebar's accordion, as plain functions.
 *
 * The menu behaves like the billing system's: every section is a heading, they
 * are all shut when somebody signs in, opening one shuts whichever was open,
 * navigating somewhere opens the section that holds the page, and a reload
 * leaves the menu exactly as it was.
 *
 * Kept out of the component so each of those rules can be tested on its own,
 * without a browser — the component is only the wiring between these and React.
 *
 * ## What is remembered, and for how long
 *
 * One section id (or none), in localStorage, keyed by the person and stamped
 * with the session it belongs to. The stamp is what makes "all shut after
 * signing in" hold for every way in — password, provider, an activation link —
 * rather than only for the paths that remember to clear it: a new session has a
 * new expiry, so whatever an older session left behind simply does not match
 * and the menu starts shut. `clearNavMenus` is called on sign-in and sign-out as
 * well, so nothing of one person's menu lingers on a shared machine.
 */

/** Prefix for the per-person key. Followed by the user id. */
export const NAV_MENU_KEY_PREFIX = 'jua.nav.section.';

/**
 * What the previous, multi-open version stored. Removed whenever menus are
 * cleared so it does not sit in somebody's browser forever.
 */
export const LEGACY_NAV_KEY = 'jua.nav.opened';

/** The parts of localStorage this needs. A fake satisfies it in tests. */
export type MenuStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

type StoredMenu = { session: string; open: string | null };

/**
 * Which session the stored menu belongs to.
 *
 * The user id plus the session's expiry. A session's expiry is fixed when it is
 * created and never slides, so it is the same across reloads and different
 * after every sign-in — exactly the line between "keep" and "start shut".
 */
export function sessionMarker(
  user: { id: string; sessionExpiresAt?: string | Date | null } | null | undefined,
): string | null {
  if (!user) return null;
  const expiry =
    user.sessionExpiresAt instanceof Date
      ? user.sessionExpiresAt.toISOString()
      : (user.sessionExpiresAt ?? '');
  return `${user.id}|${expiry}`;
}

export const navMenuKey = (userId: string): string => `${NAV_MENU_KEY_PREFIX}${userId}`;

/** Clicking a heading: open it and shut the rest, or shut it if it was open. */
export function toggleSection(open: string | null, id: string): string | null {
  return open === id ? null : id;
}

/**
 * The section to show after the page changed.
 *
 * The one holding the new page, so the item just chosen is never hidden behind
 * a shut heading. A page that is not in the menu at all (an account page, a
 * ticket opened from search) leaves the menu as it was rather than shutting
 * what somebody had open.
 */
export function sectionAfterNavigation(open: string | null, activeSection: string | null): string | null {
  return activeSection ?? open;
}

/**
 * Whether a change of page should open its section.
 *
 * Not the first page the menu is drawn on — that is a fresh sign-in or a
 * reload, and both have their own rule (shut, or as it was). And not a
 * redirect: `/login` sends a newly signed-in person to `/` with `replace`, and
 * that is the application tidying its address, not somebody choosing a page.
 */
export function shouldOpenOnNavigation(isFirstRender: boolean, navigationType: string): boolean {
  return !isFirstRender && navigationType !== 'REPLACE';
}

/**
 * Which section holds the page at `pathname`.
 *
 * Longest match wins, so `/contracts/templates` belongs to whichever section
 * lists it rather than to the one that lists `/contracts`, and `/` only ever
 * matches itself when it is marked exact.
 */
export function activeSectionFor(
  sections: readonly { id: string; items: readonly { to: string; exact?: boolean }[] }[],
  pathname: string,
): string | null {
  let best: { id: string; length: number } | null = null;
  for (const section of sections) {
    for (const item of section.items) {
      const hit = item.exact ? pathname === item.to : pathname.startsWith(item.to);
      if (hit && (!best || item.to.length > best.length)) {
        best = { id: section.id, length: item.to.length };
      }
    }
  }
  return best?.id ?? null;
}

/**
 * The section to start with when the menu is drawn.
 *
 * What this session left, if it left anything and that section is still one
 * this person can see; otherwise nothing, which is every section shut.
 */
export function restoreSection(
  storage: MenuStorage | null,
  userId: string | null | undefined,
  marker: string | null,
  visibleIds?: readonly string[],
): string | null {
  if (!storage || !userId || !marker) return null;
  let stored: StoredMenu | null = null;
  try {
    const raw = storage.getItem(navMenuKey(userId));
    stored = raw ? (JSON.parse(raw) as StoredMenu) : null;
  } catch {
    return null;
  }
  if (!stored || typeof stored !== 'object' || stored.session !== marker) return null;
  const open = typeof stored.open === 'string' ? stored.open : null;
  if (open && visibleIds && !visibleIds.includes(open)) return null;
  return open;
}

/** Remember the open section for this session. Storage refusing is not an error. */
export function saveSection(
  storage: MenuStorage | null,
  userId: string | null | undefined,
  marker: string | null,
  open: string | null,
): void {
  if (!storage || !userId || !marker) return;
  try {
    storage.setItem(navMenuKey(userId), JSON.stringify({ session: marker, open } satisfies StoredMenu));
  } catch {
    // Site data blocked. The menu still works; it just forgets on reload.
  }
}

/**
 * Forget every remembered menu in this browser — on sign-in and sign-out.
 *
 * Every person's, not only the one leaving: on a shared laptop the next person
 * should start from a shut menu whoever used it last.
 */
export function clearNavMenus(storage: MenuStorage | null): void {
  if (!storage) return;
  try {
    const doomed: string[] = [LEGACY_NAV_KEY];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(NAV_MENU_KEY_PREFIX)) doomed.push(key);
    }
    for (const key of doomed) storage.removeItem(key);
  } catch {
    // Nothing to clear if storage cannot be read.
  }
}

/** localStorage, or null where the browser refuses it or there is none. */
export function browserStorage(): MenuStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}
